import type { ServicePageConfig } from "../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../package-pricing";

export const möbelmonteringPages: ServicePageConfig[] = [
  {
    // Möbelmontering — config för /mobelmontering
    // Vinkel: Montering av plattpackade möbler (IKEA m.fl.) med komplett verktygslåda.
    // Inte transport/bärhjälp — fokus på skruva, borra, montera.
    slug: "mobelmontering",
    serviceCategory: "möbelmontering",

    meta: {
      title:
        "Möbelmontering – Snabb & Prisvärd | Ungdomskraft",
      description:
        "Boka möbelmontering i Stockholm och Göteborg. IKEA-möbler, garderober, kök och plattpack – vi har alla verktyg och monterar snabbt. Fast pris. Boka idag!",
      h1: "Möbelmontering",
    },

    hero: {
      heading: "Hjälp med möbelmontering",
      intro:
        "Slipp bråka med instruktioner och insexnycklar. Vi skickar unga montörer med komplett verktygslåda direkt till dig – ofta samma dag. Allt från en enkel bokhylla till ett helt IKEA-kök. Fast pris, enkel bokning och över 50 000 slutförda uppdrag.",
    },

    featureCards: {
      headingText: "Vi monterar dina möbler",
      sinceText: "Möbelmontering sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft monterat tusentals plattpackade möbler åt privatpersoner och företag i Stockholm och Göteborg. Vårt team av snabba ungdomar bygger allt från IKEA-garderober till kontorsmöbler – med den energi och noggrannhet som krävs för ett perfekt resultat.",
      bookYourText: "Boka din möbelmontering",
      bookDescText:
        "Beskriv vilka möbler som ska monteras, ange din adress och se priset direkt. Vi tar med alla verktyg som behövs.",
      vehicleTitleText: "Bil med komplett verktygslåda",
      vehicleDescText:
        "Våra montörer kommer med bil utrustad med komplett verktygslåda: borrmaskin, skruvdragare, insexnycklar i alla storlekar, vattenpass, gummiklubba, tumstock och allt annat som behövs för att montera dina möbler snabbt och korrekt. Du behöver inte ha några egna verktyg hemma.",
    },

    itemsSection: {
      heading: "Möbler vi monterar",
      items: [
        {
          name: "Bokhyllor & hyllsystem",
          description:
            "BILLY, KALLAX, EKET och andra bokhyllor och förvaringssystem",
          image: "/wardrobe.webp",
        },
        {
          name: "Garderober",
          description:
            "PAX, PLATSA och andra garderobssystem med skjutdörrar, lådor och inredning",
          image: "/wardrobe.webp",
        },
        {
          name: "Skrivbord & kontorsmöbler",
          description:
            "Skrivbord, höj- och sänkbara bord, kontorsstolar och hurtsar",
          image: "/office-desk.webp",
        },
        {
          name: "Sängar & sängstommar",
          description:
            "Sängstommar, loftsängar, våningssängar och huvudgavlar",
          image: "/bed.webp",
        },
        {
          name: "Matbord & stolar",
          description:
            "Matgrupper, utdragbara bord, bänkar och stolar som kräver montering",
          image: "/table.webp",
        },
        {
          name: "Köksskåp & bänkskivor",
          description:
            "METOD-kök, överskåp, underskåp, bänkskivor och köksöar",
          image: "/dishes.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Möbelmontering – från plattpack till färdig möbel på nolltid",
      mainText:
        "Att montera plattpackade möbler kan vara både tidskrävande och frustrerande. Skruvar som fattas, instruktioner som är svårtydda och verktyg du inte har hemma. Med Ungdomskraft slipper du allt det. Vi skickar erfarna montörer med komplett verktygslåda direkt till din dörr – och förvandlar högar av kartong till färdiga möbler medan du tar det lugnt.\n\nVåra ungdomar monterar allt från enstaka bokhyllor och skrivbord till hela garderobssystem och kompletta IKEA-kök. Vi har monterat tusentals PAX-garderober, KALLAX-hyllor och MALM-byråer och vet exakt vilka fallgropar som finns. Resultatet? Möbler som sitter ihop ordentligt, dörrar som stänger jämnt och lådor som glider som de ska.\n\nVi tar med borrmaskin, skruvdragare, insexnycklar i alla storlekar, vattenpass, gummiklubba, tumstock och allt annat som behövs. Du behöver inte ens ha en skruvmejsel hemma. Beskriv bara vilka möbler som ska monteras så löser vi resten.",
      preparationHeading: "Förbered inför möbelmonteringen",
      preparationText:
        "Se till att kartongerna med de plattpackade möblerna finns på plats i rätt rum innan montörerna kommer. Kontrollera gärna att alla delar finns med genom att stämma av mot innehållsförteckningen. Rensa golvet så att det finns tillräckligt med arbetsyta – en bokhylla behöver ungefär två gånger sin golvyta för montering. Om du har specifika önskemål om placering, markera gärna var möbeln ska stå. Har du köpt garderobsinredning, hyllplan eller andra tillbehör separat – lägg dem bredvid rätt kartong så att montörerna slipper leta.",
      additionalSections: [
        {
          heading: "Vad vi monterar – komplett lista",
          listItems: [
            {
              label: "IKEA-möbler –",
              text: "PAX, BILLY, KALLAX, MALM, HEMNES, BESTÅ, EKET, METOD-kök och alla andra IKEA-serier. Vi har monterat tusentals IKEA-möbler och känner igen varje skruv och plugg.",
            },
            {
              label: "Garderober och förvaringssystem –",
              text: "Garderober med skjutdörrar, walk-in closets, hatthyllor, skoinredning och belysning. Oavsett om det är PAX, Elfa eller ett annat system.",
            },
            {
              label: "Kök –",
              text: "Underskåp, överskåp, bänkskivor, köksöar, fläktinstallation (ej el/VVS) och montering av vitvaror i skåpstommar. Vi monterar köket – du beställer VVS och el separat.",
            },
            {
              label: "Kontorsmöbler –",
              text: "Höj- och sänkbara skrivbord, kontorsstolar, hurtsar, whiteboardtavlor och mötesrumsmöbler. Perfekt för kontor som flyttar in i nya lokaler.",
            },
            {
              label: "Sängar –",
              text: "Sängstommar med förvaring, loftsängar, våningssängar, huvudgavlar och kontinentalsängar som kräver ihopskruvning.",
            },
            {
              label: "Trädgårdsmöbler –",
              text: "Utemöbelgrupper, altanmöbler, förvaringsboxar och grillbord som levereras plattpackade.",
            },
          ],
          afterContent:
            "Är du osäker på om vi kan montera just din möbel? Beskriv den i bokningen så återkommer vi – vi löser det mesta.",
        },
        {
          heading: "Så fungerar möbelmontering med Ungdomskraft",
          content:
            "Boka online på ett par minuter. Beskriv vilka möbler som ska monteras och ange din adress. Du ser priset direkt och väljer en tid som passar – vi erbjuder tider vardagar, kvällar och helger. Våra montörer kommer med bil och komplett verktygslåda. De packar upp kartongerna, sorterar delarna, monterar möblerna och kontrollerar att allt sitter som det ska. Skjutdörrar justeras, lådor testas och hyllplan sätts på rätt höjd. När möblerna är klara plockar vi ihop allt emballage – kartong, cellplast och frigolit – och tar med oss det om du vill. Du slipper springa till återvinningen med berg av wellpapp. Hela processen är smidig, snabb och utan överraskningar. Du ser priset innan du bokar och betalar först när jobbet är klart.",
        },
        {
          heading: "Kombinera möbelmontering med hemleverans",
          content:
            "Har du beställt möbler från IKEA, JYSK, Mio eller en annan butik? Vi kan både hämta och montera dina möbler i samma bokning. Våra ungdomar hämtar de plattpackade kartongerna i butiken, kör hem dem till dig och monterar allt på plats. Du slipper hyra släp, bära tunga kartonger i trappor och sedan montera allt själv. Allt i en enda bokning – från butikshylla till färdig möbel i ditt hem. Det sparar tid, energi och ofta även pengar jämfört med att boka transport och montering var för sig.",
        },
        {
          heading: "Varför boka möbelmontering med Ungdomskraft?",
          content:
            "Ungdomskraft grundades 2014 med en tydlig vision: att ge unga människor en riktig chans på arbetsmarknaden, samtidigt som kunder får snabb och prisvärd hjälp. Den visionen genomsyrar fortfarande varje uppdrag vi utför – inklusive möbelmontering.\n\nVåra montörer är unga, läraktiga och genuint motiverade. De har gått igenom vår interna utbildning där de lär sig allt från att läsa monteringsinstruktioner effektivt till att hantera verktyg säkert och professionellt. Varje uppdrag är en chans att utvecklas, och det märks i kvaliteten på arbetet. De tar sig tid att göra rätt – justerar skjutdörrar tills de glider perfekt, kontrollerar att hyllplan sitter i våg och ser till att lådor öppnas och stängs utan motstånd.\n\nFör många av våra ungdomar är Ungdomskraft det första riktiga jobbet. Här lär de sig kundkontakt, punktlighet, problemlösning och att ta ansvar för ett färdigt resultat. Det är arbetslivserfarenhet som banker, IT-företag och andra arbetsgivare värderar högt när våra ungdomar senare söker vidare. Genom att boka möbelmontering med oss investerar du inte bara i färdiga möbler – du investerar i en ung människas framtid.\n\nVi tar kvalitet på allvar. Varje uppdrag avslutas med en genomgång där montören kontrollerar att alla skruvar sitter, att dörrar stänger ordentligt och att möbeln står stadigt. Om något inte känns rätt fixar vi det på plats – ingen diskussion.\n\nHållbarhet är en naturlig del av vårt arbete. Vi tar hand om allt emballage och sorterar kartong, plast och frigolit för återvinning. Företag som anlitar oss kan inkludera våra uppdrag i sin CSR-rapportering, eftersom varje bokning bidrar till ungdomssysselsättning och miljöansvar.\n\nAtt boka möbelmontering med Ungdomskraft är mer än en tjänst – det är hjälp med mening. Du får professionellt monterade möbler, stödjer ungas väg in i arbetslivet och bidrar till en mer hållbar hantering av förpackningsmaterial. Det är en vinst för alla inblandade.\n\nMöbelmontering är en RUT-berättigad tjänst, vilket innebär att du får avdrag på 50 % av arbetskostnaden – upp till 75 000 kr per person och år. Avdraget hanteras direkt på fakturan, så du betalar bara halva priset för arbetet. Det gör professionell möbelmontering till ett ännu smartare val.",
        },
      ],
    },

    faq: [
      {
        question: "Vilka verktyg tar ni med till möbelmonteringen?",
        answer:
          "Vi tar med komplett verktygslåda: borrmaskin, skruvdragare, insexnycklar i alla storlekar, vattenpass, gummiklubba, tumstock, hammare och allt annat som behövs. Du behöver inte ha några egna verktyg.",
      },
      {
        question: "Monterar ni IKEA-möbler?",
        answer:
          "Ja, IKEA-möbler är vår vanligaste möbelmontering. Vi har monterat tusentals PAX-garderober, BILLY-bokhyllor, KALLAX-hyllor, MALM-byråer och METOD-kök. Vi känner igen varje serie och vet exakt hur de ska byggas.",
      },
      {
        question: "Kan ni montera möbler samma dag?",
        answer:
          "Ja, vi erbjuder möbelmontering samma dag om du bokar tidigt. Våra montörer kan ofta vara på plats inom några timmar. Du ser tillgängliga tider direkt vid bokning.",
      },
      {
        question: "Vad kostar möbelmontering?",
        answer:
          "Priset beror på antal möbler och komplexitet. En enstaka möbel som en bokhylla eller säng från 1 495 kr (Liten). 2–3 möbler från 2 495 kr (Mellan). Större monteringsprojekt på 4–6 möbler från 3 995 kr (Stor). Komplett kök eller många rum 5 995 kr (Extra Stor). Du ser alltid det uppskattade priset innan du bokar.",
      },
      {
        question: "Kan jag få RUT-avdrag på möbelmontering?",
        answer:
          "Ja, möbelmontering ger rätt till RUT-avdrag. Du får avdrag på 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget gäller montering och demontering av möbler i din bostad – exempelvis IKEA-garderober, sängar och köksskåp. Vi hanterar RUT-avdraget direkt på fakturan, så du betalar bara halva arbetskostnaden.",
      },
      {
        question: "Är mina möbler försäkrade under monteringen?",
        answer:
          "Ja, alla uppdrag är försäkrade. Om något skulle gå sönder under möbelmonteringen ersätter vi det. Våra montörer arbetar noggrant och varsamt med dina möbler.",
      },
      {
        question: "Tar ni hand om förpackningsmaterialet efter möbelmonteringen?",
        answer:
          "Ja, vi plockar ihop all kartong, cellplast och frigolit efter monteringen. Om du vill kan vi ta med oss allt emballage så slipper du springa till återvinningen.",
      },
      {
        question: "Kan ni montera möbler som inte är från IKEA?",
        answer:
          "Ja, vi monterar plattpackade möbler från alla tillverkare – JYSK, Mio, Rusta, Elfa och andra. Principen är densamma oavsett märke: vi läser instruktionerna och monterar möbeln korrekt.",
      },
      {
        question: "Hur lång tid tar det att montera en garderob?",
        answer:
          "En standard PAX-garderob med två dörrar tar cirka 45–90 minuter. Större system med skjutdörrar, lådor och inredning kan ta 2–3 timmar. Vi meddelar uppskattad tid vid bokning.",
      },
      {
        question: "Kan ni montera kök?",
        answer:
          "Ja, vi monterar köksskåp, bänkskivor och köksöar. Vi hanterar allt snickeri – underskåp, överskåp, luckor, lådor och beslag. El- och VVS-anslutningar behöver du beställa separat av behörig installatör.",
      },
      {
        question: "Monterar ni möbler på kvällar och helger?",
        answer:
          "Ja, vi erbjuder möbelmontering vardagar, kvällar och helger. Du väljer den tid som passar dig bäst direkt vid bokning.",
      },
      {
        question: "Kan ni demontera möbler också?",
        answer:
          "Ja, vi både monterar och demonterar möbler. Perfekt om du ska flytta och behöver ta isär garderober, sängar eller skrivbord innan flytten.",
      },
      {
        question: "Kan ni hämta möblerna i butik och montera hemma?",
        answer:
          "Ja, vi kan hämta plattpackade möbler från IKEA, JYSK, Mio eller annan butik, köra hem dem till dig och montera allt på plats – i en enda bokning.",
      },
      {
        question: "Behöver jag vara hemma under möbelmonteringen?",
        answer:
          "Du behöver vara hemma i början för att visa var möblerna ska stå och i slutet för att godkänna resultatet. Däremellan kan du göra annat – våra montörer sköter resten.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni möbelmontering?",
        answer:
          "Vi erbjuder möbelmontering i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.montering,

    pricingRows: getPackagePricingRows("montering", "sv"),

    dynamicHeadings: {
      threeSteps: "Möbelmontering i 3 enkla steg",
      step2Title: "Vi monterar allt",
      step2Text:
        "Våra montörer kommer med bil och komplett verktygslåda. De packar upp, monterar och kontrollerar att allt sitter perfekt. Kartong och emballage plockas ihop.",
      step3Title: "Klart",
      step3Desc:
        "Dina möbler är monterade, justerade och redo att användas. Emballaget är bortplockat och du kan njuta av resultatet.",
      ctaHeading: "Boka möbelmontering idag",
      ctaText:
        "Beskriv vilka möbler som ska monteras, ange din adress och se priset direkt. Vi tar med alla verktyg – du behöver inte lyfta ett finger.",
    },

    en: {
      slug: "furniture-assembly",
      meta: {
        title:
          "Furniture Assembly – Fast & Affordable | Ungdomskraft",
        description:
          "Book furniture assembly in Stockholm & Gothenburg. IKEA, wardrobes, kitchens and flat-pack – we bring all tools and assemble fast. Fixed price. Book today!",
        h1: "Furniture Assembly",
      },

      hero: {
        heading: "Help with furniture assembly",
        intro:
          "Skip the struggle with instructions and Allen keys. We send young assemblers with a complete toolbox straight to your door – often the same day. From a simple bookshelf to a full IKEA kitchen. Fixed price, easy booking and over 50,000 completed jobs.",
      },

      featureCards: {
        headingText: "We assemble your furniture",
        sinceText: "Furniture assembly since 2014",
        storyText:
          "Since 2014, Ungdomskraft has assembled thousands of flat-pack furniture pieces for individuals and businesses in Stockholm and Gothenburg. Our team of quick young assemblers builds everything from IKEA wardrobes to office furniture – with the energy and precision required for a perfect result.",
        bookYourText: "Book your assembly",
        bookDescText:
          "Describe what furniture needs assembling, enter your address and see the price instantly. We bring all necessary tools.",
        vehicleTitleText: "Car with a complete toolbox",
        vehicleDescText:
          "Our assemblers arrive by car equipped with a complete toolbox: power drill, screwdriver, Allen keys in all sizes, spirit level, rubber mallet, tape measure and everything else needed to assemble your furniture quickly and correctly. You don't need any tools at home.",
      },

      itemsSection: {
        heading: "Furniture we assemble",
        items: [
          {
            name: "Bookshelves & storage",
            description:
              "BILLY, KALLAX, EKET and other bookshelves and storage systems",
            image: "/wardrobe.webp",
          },
          {
            name: "Wardrobes",
            description:
              "PAX, PLATSA and other wardrobe systems with sliding doors, drawers and fittings",
            image: "/wardrobe.webp",
          },
          {
            name: "Desks & office furniture",
            description:
              "Desks, sit-stand desks, office chairs and drawer units",
            image: "/office-desk.webp",
          },
          {
            name: "Beds & bed frames",
            description:
              "Bed frames, loft beds, bunk beds and headboards",
            image: "/bed.webp",
          },
          {
            name: "Dining tables & chairs",
            description:
              "Dining sets, extendable tables, benches and chairs requiring assembly",
            image: "/table.webp",
          },
          {
            name: "Kitchen cabinets & countertops",
            description:
              "METOD kitchens, wall cabinets, base cabinets, countertops and kitchen islands",
            image: "/dishes.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Furniture assembly – from flat-pack to finished furniture in no time",
        mainText:
          "Assembling flat-pack furniture can be both time-consuming and frustrating. Missing screws, confusing instructions and tools you don't have at home. With Ungdomskraft, you skip all of that. We send experienced assemblers with a complete toolbox straight to your door – turning piles of cardboard into finished furniture while you relax.\n\nOur young team assembles everything from single bookshelves and desks to full wardrobe systems and complete IKEA kitchens. We have assembled thousands of PAX wardrobes, KALLAX shelves and MALM dressers and know exactly what pitfalls to watch out for. The result? Furniture that holds together properly, doors that close evenly and drawers that glide as they should.\n\nWe bring power drills, screwdrivers, Allen keys in all sizes, spirit levels, rubber mallets, tape measures and everything else needed. You don't even need a screwdriver at home. Just describe what furniture needs assembling and we handle the rest.",
        preparationHeading: "How to prepare for the assembly",
        preparationText:
          "Make sure the boxes with the flat-pack furniture are at the right location before the assemblers arrive. Check that all parts are included by reviewing the contents list. Clear the floor to provide enough workspace – a bookshelf needs roughly twice its floor area for assembly. If you have specific placement preferences, mark where the furniture should go. If you purchased wardrobe fittings, shelf inserts or other accessories separately, place them next to the matching box so the assemblers don't have to search.",
        additionalSections: [
          {
            heading: "What we assemble – complete list",
            listItems: [
              {
                label: "IKEA furniture –",
                text: "PAX, BILLY, KALLAX, MALM, HEMNES, BESTÅ, EKET, METOD kitchens and all other IKEA ranges. We have assembled thousands of IKEA pieces and recognize every screw and dowel.",
              },
              {
                label: "Wardrobes and storage systems –",
                text: "Wardrobes with sliding doors, walk-in closets, hat shelves, shoe racks and lighting. Whether it's PAX, Elfa or another system.",
              },
              {
                label: "Kitchens –",
                text: "Base cabinets, wall cabinets, countertops, kitchen islands, extractor installation (not electrical/plumbing) and fitting appliances into cabinet frames. We assemble the kitchen – you order plumbing and electrical separately.",
              },
              {
                label: "Office furniture –",
                text: "Sit-stand desks, office chairs, drawer units, whiteboards and meeting room furniture. Perfect for offices moving into new premises.",
              },
              {
                label: "Beds –",
                text: "Bed frames with storage, loft beds, bunk beds, headboards and divan beds requiring assembly.",
              },
              {
                label: "Garden furniture –",
                text: "Outdoor dining sets, patio furniture, storage boxes and grill tables delivered flat-packed.",
              },
            ],
            afterContent:
              "Not sure if we can assemble your particular piece? Describe it in the booking and we'll get back to you – we handle most things.",
          },
          {
            heading: "How furniture assembly works with Ungdomskraft",
            content:
              "Book online in a couple of minutes. Describe what furniture needs assembling and enter your address. You see the price instantly and pick a time that works – we offer slots on weekdays, evenings and weekends. Our assemblers arrive by car with a complete toolbox. They unpack the boxes, sort the parts, assemble the furniture and check that everything fits perfectly. Sliding doors are adjusted, drawers are tested and shelves are placed at the right height. When the furniture is done, we tidy up all packaging – cardboard, polystyrene and plastic wrap – and take it with us if you want. You skip the trip to the recycling station with mountains of cardboard. The whole process is smooth, fast and free of surprises. You see the price before you book and pay only when the job is done.",
          },
          {
            heading: "Combine furniture assembly with home delivery",
            content:
              "Ordered furniture from IKEA, JYSK, Mio or another store? We can both pick up and assemble your furniture in the same booking. Our team collects the flat-pack boxes from the store, drives them to your home and assembles everything on site. You skip renting a trailer, carrying heavy boxes up stairs and then assembling everything yourself. All in one booking – from store shelf to finished furniture in your home. It saves time, energy and often money compared to booking transport and assembly separately.",
          },
          {
            heading: "Why book furniture assembly with Ungdomskraft?",
            content:
              "Ungdomskraft was founded in 2014 with a clear vision: to give young people a real chance in the job market while offering customers fast and affordable help. That vision still guides every job we do – including furniture assembly.\n\nOur assemblers are young, eager to learn and genuinely motivated. They have completed our internal training programme where they learn everything from reading assembly instructions efficiently to handling tools safely and professionally. Every job is a chance to grow, and it shows in the quality of the work. They take the time to get it right – adjusting sliding doors until they glide perfectly, checking that shelves are level and making sure drawers open and close without resistance.\n\nFor many of our young workers, Ungdomskraft is their first real job. Here they learn customer interaction, punctuality, problem-solving and taking responsibility for a finished result. This is work experience that banks, tech companies and other employers value highly when our young people later apply elsewhere. By booking furniture assembly with us, you are not just investing in finished furniture – you are investing in a young person's future.\n\nWe take quality seriously. Every job ends with a walkthrough where the assembler checks that all screws are tight, doors close properly and the furniture stands steady. If anything doesn't feel right, we fix it on the spot – no discussion.\n\nSustainability is a natural part of our work. We handle all packaging and sort cardboard, plastic and polystyrene for recycling. Companies that hire us can include our jobs in their CSR reporting, since every booking contributes to youth employment and environmental responsibility.\n\nBooking furniture assembly with Ungdomskraft is more than a service – it is help with purpose. You get professionally assembled furniture, support young people entering the workforce and contribute to more sustainable handling of packaging materials. It is a win for everyone involved.\n\nFurniture assembly is eligible for the Swedish RUT tax deduction, which means you get a 50% deduction on the labour cost – up to SEK 75,000 per person per year. The deduction is handled directly on the invoice, so you only pay half the price for the work. That makes professional furniture assembly an even smarter choice.",
          },
        ],
      },

      faq: [
        {
          question: "What tools do you bring for furniture assembly?",
          answer:
            "We bring a complete toolbox: power drill, screwdriver, Allen keys in all sizes, spirit level, rubber mallet, tape measure, hammer and everything else needed. You don't need any tools at home.",
        },
        {
          question: "Do you assemble IKEA furniture?",
          answer:
            "Yes, IKEA furniture is our most common assembly job. We have assembled thousands of PAX wardrobes, BILLY bookshelves, KALLAX shelves, MALM dressers and METOD kitchens. We know every range and exactly how to build them.",
        },
        {
          question: "Can you assemble furniture the same day?",
          answer:
            "Yes, we offer same-day furniture assembly if you book early. Our assemblers can often be there within a few hours. You see available times when booking.",
        },
        {
          question: "How much does furniture assembly cost?",
          answer:
            "The price depends on the number of pieces and complexity. A single piece like a bookshelf or bed from SEK 1,495 (Small). 2–3 pieces from SEK 2,495 (Medium). Larger assembly projects of 4–6 pieces from SEK 3,995 (Large). Complete kitchen or multiple rooms SEK 5,995 (Extra Large). You always see the estimated price before you book.",
        },
        {
          question: "Can I get a RUT tax deduction on furniture assembly?",
          answer:
            "Yes, furniture assembly qualifies for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost, up to SEK 75,000 per person per year. The deduction applies to assembly and disassembly of furniture in your home – for example IKEA wardrobes, beds and kitchen cabinets. We handle the RUT deduction directly on the invoice, so you only pay half the labour cost.",
        },
        {
          question: "Is my furniture insured during assembly?",
          answer:
            "Yes, all jobs are insured. If anything gets damaged during furniture assembly, we cover it. Our assemblers work carefully and handle your furniture with care.",
        },
        {
          question: "Do you remove the packaging after furniture assembly?",
          answer:
            "Yes, we tidy up all cardboard, polystyrene and plastic wrap after assembly. If you want, we can take all packaging with us so you skip the recycling trip.",
        },
        {
          question: "Can you assemble furniture that is not from IKEA?",
          answer:
            "Yes, we assemble flat-pack furniture from all manufacturers – JYSK, Mio, Rusta, Elfa and others. The principle is the same regardless of brand: we read the instructions and assemble the furniture correctly.",
        },
        {
          question: "How long does it take to assemble a wardrobe?",
          answer:
            "A standard PAX wardrobe with two doors takes about 45–90 minutes. Larger systems with sliding doors, drawers and fittings can take 2–3 hours. We provide an estimated time when you book.",
        },
        {
          question: "Can you assemble kitchens?",
          answer:
            "Yes, we assemble kitchen cabinets, countertops and kitchen islands. We handle all carpentry – base cabinets, wall cabinets, doors, drawers and fittings. Electrical and plumbing connections need to be ordered separately from a certified installer.",
        },
        {
          question: "Do you assemble furniture on evenings and weekends?",
          answer:
            "Yes, we offer furniture assembly on weekdays, evenings and weekends. Pick the time that works best for you when booking.",
        },
        {
          question: "Can you disassemble furniture too?",
          answer:
            "Yes, we both assemble and disassemble furniture. Perfect if you are moving and need to take apart wardrobes, beds or desks before the move.",
        },
        {
          question: "Can you pick up furniture from the store and assemble it at home?",
          answer:
            "Yes, we can pick up flat-pack furniture from IKEA, JYSK, Mio or another store, drive it to your home and assemble everything on site – all in one booking.",
        },
        {
          question: "Do I need to be home during furniture assembly?",
          answer:
            "You need to be home at the start to show where the furniture should go, and at the end to approve the result. In between, you can do other things – our assemblers handle the rest.",
        },
        {
          question:
            "In which municipalities do you offer furniture assembly?",
          answer:
            "We offer furniture assembly across the entire Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.montering,

      pricingRows: getPackagePricingRows("montering", "en"),

      dynamicHeadings: {
        threeSteps: "Furniture assembly in 3 easy steps",
        step2Title: "We assemble everything",
        step2Text:
          "Our assemblers arrive by car with a complete toolbox. They unpack, assemble and check that everything fits perfectly. Packaging is tidied up.",
        step3Title: "Done",
        step3Desc:
          "Your furniture is assembled, adjusted and ready to use. Packaging has been cleared away and you can enjoy the result.",
        ctaHeading: "Book furniture assembly today",
        ctaText:
          "Describe what furniture needs assembling, enter your address and see the price instantly. We bring all the tools – you don't need to lift a finger.",
      },
    },
  },
];
